import React from 'react';
import './LegalPage.css';

interface AboutPageProps {
    onClose: () => void;
}

const AboutPage: React.FC<AboutPageProps> = ({ onClose }) => {
    return (
        <div className="legal-overlay" onClick={onClose}>
            <div className="legal-container" onClick={(e) => e.stopPropagation()}>
                <button className="legal-close-btn" onClick={onClose} aria-label="Close">
                    <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                </button>

                <div className="legal-content">
                    <h1>About CensusLens</h1>
                    <p className="legal-subtitle">US Demographic Intelligence Platform</p>

                    <section>
                        <h2>Our Mission</h2>
                        <p>
                            CensusLens transforms complex U.S. Census Bureau data into intuitive, interactive visualizations that empower researchers, policymakers, businesses, and citizens to understand demographic trends across America.
                        </p>
                        <p>
                            We believe that access to demographic intelligence should be simple, beautiful, and actionable. By combining cutting-edge web mapping technology with authoritative government data, we make it easy to explore everything from median household income to population density at the county level.
                        </p>
                    </section>

                    <section>
                        <h2>What We Offer</h2>
                        <div className="feature-grid">
                            <div className="feature-card">
                                <div className="feature-icon">📊</div>
                                <h3>Interactive Heatmaps</h3>
                                <p>Visualize demographic data with color-coded maps that reveal patterns and trends at a glance.</p>
                            </div>
                            <div className="feature-card">
                                <div className="feature-icon">🗺️</div>
                                <h3>County-Level Precision</h3>
                                <p>Explore data for all 3,000+ U.S. counties with detailed tooltips and metrics.</p>
                            </div>
                            <div className="feature-card">
                                <div className="feature-icon">📈</div>
                                <h3>Multiple Data Metrics</h3>
                                <p>Analyze median income, population, poverty rates, education levels, and more.</p>
                            </div>
                            <div className="feature-card">
                                <div className="feature-icon">🔍</div>
                                <h3>State-Level Filtering</h3>
                                <p>Focus on specific states or view the entire United States in one comprehensive view.</p>
                            </div>
                        </div>
                    </section>

                    <section>
                        <h2>Data Sources</h2>
                        <p>
                            All demographic data displayed on CensusLens is sourced directly from the <strong>U.S. Census Bureau</strong>, the nation's leading provider of quality data about its people and economy. We utilize:
                        </p>
                        <ul>
                            <li><strong>American Community Survey (ACS):</strong> Annual estimates of demographic, social, economic, and housing characteristics</li>
                            <li><strong>Decennial Census:</strong> Comprehensive population counts conducted every 10 years</li>
                            <li><strong>Census Bureau APIs:</strong> Real-time access to the latest available datasets</li>
                        </ul>
                        <p>
                            Our platform does not modify or interpret census data—we present official government statistics in an accessible format.
                        </p>
                    </section>

                    <section>
                        <h2>Technology Stack</h2>
                        <p>CensusLens is built with modern web technologies to ensure a fast, responsive, and reliable experience:</p>
                        <ul>
                            <li><strong>React & TypeScript:</strong> Component-based architecture for maintainable code</li>
                            <li><strong>OpenLayers:</strong> Industry-leading open-source mapping library</li>
                            <li><strong>Vite:</strong> Lightning-fast build tool for optimal performance</li>
                            <li><strong>Vercel:</strong> Global edge network for instant page loads</li>
                        </ul>
                    </section>

                    <section>
                        <h2>Use Cases</h2>
                        <p>CensusLens serves a diverse range of users and applications:</p>
                        <ul>
                            <li><strong>Researchers & Academics:</strong> Identify demographic trends for studies and publications</li>
                            <li><strong>Real Estate Professionals:</strong> Assess market conditions and neighborhood characteristics</li>
                            <li><strong>Business Analysts:</strong> Make data-driven decisions about market expansion and targeting</li>
                            <li><strong>Policymakers & Nonprofits:</strong> Understand community needs and allocate resources effectively</li>
                            <li><strong>Journalists:</strong> Create compelling data-driven stories about American communities</li>
                            <li><strong>Educators & Students:</strong> Learn about geography, demographics, and data visualization</li>
                        </ul>
                    </section>

                    <section>
                        <h2>Our Commitment</h2>
                        <p>We are committed to:</p>
                        <ul>
                            <li><strong>Accuracy:</strong> Displaying official, unmodified census data from authoritative sources</li>
                            <li><strong>Transparency:</strong> Clearly documenting data sources, methodologies, and limitations</li>
                            <li><strong>Accessibility:</strong> Ensuring our platform is usable by people of all abilities</li>
                            <li><strong>Privacy:</strong> Protecting user privacy and never collecting personal demographic information</li>
                            <li><strong>Continuous Improvement:</strong> Regularly updating data and adding new features based on user feedback</li>
                        </ul>
                    </section>

                    <section>
                        <h2>Roadmap</h2>
                        <p>We're constantly working to enhance CensusLens. Upcoming features include:</p>
                        <ul>
                            <li>ZIP code-level data visualization</li>
                            <li>Historical trend analysis and time-series comparisons</li>
                            <li>Custom data export and reporting tools</li>
                            <li>Additional demographic metrics (housing, employment, health)</li>
                            <li>Mobile app for iOS and Android</li>
                        </ul>
                    </section>

                    <section>
                        <h2>Contact & Support</h2>
                        <p>We'd love to hear from you! Whether you have questions, feedback, or partnership inquiries:</p>
                        <p className="contact-info">
                            <strong>Email:</strong> censuslensapp@gmail.com<br />
                            <strong>Website:</strong> https://censuslens.com<br />
                            <strong>GitHub:</strong> github.com/mmccord21/vercel-census-atlas
                        </p>
                    </section>

                    <section>
                        <h2>Disclaimer</h2>
                        <p>
                            CensusLens is an independent platform and is not affiliated with, endorsed by, or sponsored by the U.S. Census Bureau or any government agency. All census data is publicly available and subject to the terms and conditions of the U.S. Census Bureau.
                        </p>
                        <p>
                            While we strive for accuracy, demographic data may contain sampling errors, estimation uncertainties, or reporting delays. Users should consult official Census Bureau resources for authoritative information and detailed methodology documentation.
                        </p>
                    </section>

                    <section className="legal-footer">
                        <p>
                            <strong>Version:</strong> 1.2.0<br />
                            <strong>Last Updated:</strong> February 15, 2026
                        </p>
                    </section>
                </div>
            </div>
        </div>
    );
};

export default AboutPage;
